<style type="text/css">
	.ql-editor{
	  height: 420px;
	  max-height: 420px;
	  overflow: auto;
	}
</style>
<template>
	<div class="edit-form__item quill-wrapper">
		<input type="file" ref="upload" @change="onUploadImage" style="display: none" />
		<quill-editor
			ref="myQuillEditor"
			:content="model[schema.model]"
			:disabled="!editPermission"
			:options="editorOption"
			@change="onEditorChange"
		/>
		<span v-if="inputError" class="error-message">
			<icon icon="status-error" />
			{{ uploadErrorMessage }}
		</span>
	</div>
</template>

<script>
import { abstractField } from '@backstagex-vue-form-generator/core';
import { quillEditor } from 'vue-quill-editor';
import { apiPath, config } from '@/config.js';
import debounce from 'lodash/debounce';

import { setFileUploadOptions, uploadToLocal, uploadToS3 } from '@/utils/edit-page';
import { convertFileSizeToMB } from '@/utils/file';
import { mapGetters } from 'vuex';

import icon from '@/components/atoms/Icon';
import Quill from 'quill';

export default {
	mixins: [abstractField],
	props: ['schema', 'model'],
	components: {
		quillEditor,
		icon,
	},
	data() {
		// default modules for quill editor
		const modules = {
			toolbar: [
				['bold', 'italic', 'underline', 'strike'],
				[{ list: 'ordered' }, { list: 'bullet' }],
				[{ align: [] }],
				[{ header: [1, 2, 3, false] }],
				[{ color: [] }, { background: [] }],
				['link', 'image'],
				['clean'],
			],
			clipboard: {
				matchVisual: false,
			},
		};

		return {
			editorOption: {
				theme: this.schema.theme ?? 'snow',
				placeholder: this.schema.placeholder ?? '請輸入內容',
				modules: this.schema.modules ?? modules,
			},
			contentText: this.model[this.schema.model] || '\n',
			inputError: false,
			uploadErrorMessage: '',
			uploadConfig: {
				accept: '.doc,.docx,.pdf,audio/*,video/*,image/*,text/*',
				folder: 'upload',
				sizeLimit: 1,
				uploadMethod: 'direct',
			},
		};
	},
	computed: {
		...mapGetters('model', {
			config: 'config',
		}),
		editPermission() {
			return this.$store.getters['model/editPermission'];
		},
	},
	created() {
		// initialize uploadConfig
		const { sizeLimit, accept, folder, uploadMethod } = setFileUploadOptions(
			this.config?.uploadConfig?.[this.schema?.uploadConfig],
			this.schema
		);
		this.uploadConfig = { sizeLimit, accept, folder, uploadMethod };
	},
	mounted() {
		this.$refs.myQuillEditor.quill.getModule('toolbar').addHandler('image', this.imageHandler);
		let AlignStyle = Quill.import('attributors/style/align');
		Quill.register(AlignStyle, true);
	},
	methods: {
		onEditorChange: debounce(function ({ html }) {
			this.inputError = false;
			this.contentText = html;
			this.model[this.schema.model] = html;
			this.validation();
		}, 100),
		imageHandler() {
			this.$refs.upload.click();
		},
		async onUploadImage(event) {
			// check file
			const file = event.target.files[0];
			const fileSize = convertFileSizeToMB(file.size);
			const { sizeLimit } = this.uploadConfig;

			if (fileSize > sizeLimit) {
				this.$store.commit('popUp/alert', {
					type: 'warning',
					label: this.$t('bsxPage.vue_form.upload_error'),
					value: this.$t('bsxPage.vue_form.support_file_size', { sizeLimit }),
				});
				return;
			}

			// uploadToLocal params
			let formData = new FormData();
			formData.append('file', file);
			formData.append('name', file.name);

			// uploadToS3 params
			const extension = file.name.split('.').pop();
			const apiEndPoint = 'upload/upload-url';
			const uploadUrlApiUrl = apiPath(false) + apiEndPoint;
			const uploadUrlApiBody = {
				type: this.schema.uploadConfig,
				extension,
			};

			// get upload file
			const response = await (this.uploadMethod === 's3'
				? uploadToS3(uploadUrlApiUrl, uploadUrlApiBody, {}, file)
				: uploadToLocal(apiPath(false) + 'upload/quill', formData));

			if (!response || response.status < 200 || response.status >= 300) {
				console.error('Upload failed', response?.data?.message);
				return;
			}

			const { filename } = response;
			const addImgRange = this.$refs.myQuillEditor.quill.getSelection();
			this.$refs.myQuillEditor.quill.insertEmbed(
				addImgRange != null ? addImgRange.index : 0,
				'image',
				config.storageHost + filename
			);
		},
		validation() {
			if (this.schema.required && this.contentText === '\n') {
				this.inputError = true;
				this.uploadErrorMessage = this.$t('validation.field_is_required');
			}
		},
	},
};
</script>
<style scoped lang="scss" src="./style.scss" />

<style>
/*
// The ql-clipboard is dynamically generated, so it cannot be placed in scoped styles.
// It will become ineffective due to the lack of corresponding hash.
// fix bug: quill editor Pasting in content causes scroll to jump
*/
.edit-form__item.quill-wrapper .ql-clipboard {
	position: fixed !important;
	display: none;
	left: 50% !important;
	top: 50% !important;
}
</style>
